/**
 * AutoCount - Inventory List Page
 * Live stock position for the signed-in user's warehouse.
 */

import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { listInventory, InventoryItem, ApiError } from "api";
import { useAuth } from "context/auth/authContext";

function SummaryCard({
  value,
  label,
  color,
}: {
  value: number;
  label: string;
  color: "info" | "success" | "warning" | "error";
}): JSX.Element {
  return (
    <Card>
      <MDBox p={2} textAlign="center">
        <MDTypography variant="h3" color={color}>
          {value}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          {label}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

function InventoryList(): JSX.Element {
  const { authState } = useAuth();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listInventory({ pageSize: 200 })
      .then((response) => {
        if (cancelled) return;
        setItems(response.items);
        setTotal(response.total);
        setLoading(false);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const totalUnits = useMemo(
    () => items.reduce((sum, item) => sum + item.quantityOnHand, 0),
    [items]
  );
  const outOfStock = useMemo(
    () => items.filter((item) => item.quantityAvailable === 0).length,
    [items]
  );
  const pendingCounts = useMemo(
    () => items.filter((item) => item.variance !== 0).length,
    [items]
  );

  const columns = useMemo(
    () => [
      { Header: "Part Number", accessor: "partNumber", width: "12%" },
      { Header: "Name", accessor: "name", width: "26%" },
      { Header: "On Hand", accessor: "onHand", width: "9%" },
      { Header: "Reserved", accessor: "reserved", width: "9%" },
      { Header: "Available", accessor: "available", width: "9%" },
      { Header: "On Order", accessor: "onOrder", width: "9%" },
      { Header: "Last Count", accessor: "lastCount", width: "12%" },
      { Header: "Bin", accessor: "location", width: "8%" },
    ],
    []
  );

  const rows = useMemo(
    () =>
      items.map((item) => ({
        partNumber: item.partNumber,
        name: item.partName,
        onHand: item.quantityOnHand,
        reserved: item.quantityReserved,
        available: (
          <MDTypography
            variant="caption"
            color={
              item.quantityAvailable > 10
                ? "success"
                : item.quantityAvailable > 0
                ? "warning"
                : "error"
            }
            fontWeight="medium"
          >
            {item.quantityAvailable}
          </MDTypography>
        ),
        onOrder: item.quantityOnOrder,
        lastCount:
          item.lastCountQty === null ? (
            "—"
          ) : (
            <MDTypography
              variant="caption"
              color={item.variance === 0 ? "success" : "warning"}
              fontWeight="medium"
            >
              {item.lastCountQty}
              {item.variance !== 0 &&
                ` (${item.variance > 0 ? "+" : ""}${item.variance})`}
            </MDTypography>
          ),
        location: item.binLocation || "—",
      })),
    [items]
  );

  const warehouseName =
    authState.user?.defaultWarehouse?.name || "Your Warehouse";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} md={3}>
            <SummaryCard value={total} label="Stocked SKUs" color="info" />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryCard value={totalUnits} label="Total Units" color="success" />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryCard
              value={pendingCounts}
              label="Count Variances"
              color="warning"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryCard value={outOfStock} label="Out of Stock" color="error" />
          </Grid>

          {/* Inventory Table */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Inventory Levels — {warehouseName}
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                {error && (
                  <MDBox px={3} pb={2}>
                    <Alert severity="error">{error}</Alert>
                  </MDBox>
                )}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={6}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                    showTotalEntries={true}
                    canSearch={true}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InventoryList;
